/* Cruise Packages tab , edit every package tier, inclusions, menu (line-by-line),
   beverage list and rules. Notion-style: click any text, edit, blur to save.
   Each save = full PUT to /admin/cms/packages/{cruise_type}.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Package, Plus, Trash2, ChevronDown, ChevronRight, RefreshCcw, Check } from "lucide-react";
import { adminApi } from "./adminApi";
import InlineEdit from "./InlineEdit";

const CRUISE_TYPES = ["birthday", "wedding", "corporate", "christmas", "byo"];

export default function AdminPackages({ onUnauthorized, showToast }) {
 const [pkgs, setPkgs] = useState({});
 const [activeType, setActiveType] = useState("birthday");
 const [openTiers, setOpenTiers] = useState({}); // {cruiseType_tierIdx: bool}
 const [loading, setLoading] = useState(true);
 const [saveBlinks, setSaveBlinks] = useState({}); // cruiseType: timestamp

 const load = useCallback(async () => {
  setLoading(true);
  try {
   const res = await adminApi("/admin/cms/packages");
   const map = {};
   (res.packages || []).forEach((p) => { map[p.cruise_type] = p; });
   setPkgs(map);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Failed to load packages", "err");
  } finally { setLoading(false); }
 }, [onUnauthorized, showToast]);

 useEffect(() => { load(); }, [load]);

 const data = pkgs[activeType];

 // Persists the entire package object (full-object PUT pattern).
 // Optimistically updates local state, rolls back on failure.
 const persist = async (cruiseType, mutator) => {
  const current = pkgs[cruiseType];
  if (!current) return;
  const draft = mutator(JSON.parse(JSON.stringify(current)));
  const body = {
   title: draft.title || "",
   intro: draft.intro || "",
   accent: draft.accent || "rose",
   photo: draft.photo || "",
   photoAlt: draft.photoAlt || "",
   packages: draft.packages || [],
   faqs: draft.faqs || [],
  };
  setPkgs((prev) => ({ ...prev, [cruiseType]: { ...prev[cruiseType], ...body } }));
  try {
   const updated = await adminApi(`/admin/cms/packages/${cruiseType}`, { method: "PUT", body });
   setPkgs((prev) => ({ ...prev, [cruiseType]: updated }));
   setSaveBlinks((prev) => ({ ...prev, [cruiseType]: Date.now() }));
   setTimeout(() => setSaveBlinks((prev) => { const n = { ...prev }; delete n[cruiseType]; return n; }), 2200);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Save failed", "err");
   // Roll back to server truth
   setPkgs((prev) => ({ ...prev, [cruiseType]: current }));
   throw e;
  }
 };

 const tierKey = (i) => `${activeType}_${i}`;
 const toggleTier = (i) => setOpenTiers((p) => ({ ...p, [tierKey(i)]: !p[tierKey(i)] }));

 return (
  <div data-testid="admin-tab-packages">
   <div className="flex items-start justify-between gap-4 mb-6">
    <div>
     <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-2">
      <Package className="w-3.5 h-3.5" /> Cruise Packages & Menus
     </div>
     <p className="text-white/65 text-sm max-w-2xl">
      Edit every package tier, inclusion line, menu line and beverage line live. Click any field, edit, blur to save. Changes update the public site instantly.
     </p>
    </div>
    <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/75 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid="admin-packages-refresh"><RefreshCcw className="w-3 h-3" /> Refresh</button>
   </div>

   {/* Cruise type tabs */}
   <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4" data-testid="admin-pkg-tabs">
    {CRUISE_TYPES.map((t) => (
     <button
      key={t}
      onClick={() => setActiveType(t)}
      className={`relative px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] font-black transition-colors ${activeType === t ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/70 hover:bg-white/[0.06]"}`}
      data-testid={`admin-pkg-tab-${t}`}
     >
      {t}
      {saveBlinks[t] ? <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 bg-emerald-500 text-white px-1.5 py-0.5 rounded-full text-[8px] uppercase tracking-wider font-black"><Check className="w-2.5 h-2.5" /></span> : null}
     </button>
    ))}
   </div>

   {loading || !data ? (
    <div className="text-white/45 text-sm py-12 text-center">Loading…</div>
   ) : (
    <div className="space-y-8">
     {/* Header info */}
     <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 lg:p-6">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-start">
       <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/45">Title</div>
        <div className="text-xl lg:text-2xl font-black tracking-tight">
         <InlineEdit
          value={data.title}
          onSave={(v) => persist(activeType, (d) => { d.title = v; return d; })}
          testid={`admin-pkg-${activeType}-title`}
         />
        </div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/45 pt-2">Intro</div>
        <div className="text-sm text-white/80 leading-relaxed">
         <InlineEdit
          multiline
          value={data.intro}
          onSave={(v) => persist(activeType, (d) => { d.intro = v; return d; })}
          testid={`admin-pkg-${activeType}-intro`}
         />
        </div>
       </div>
       <div className="text-[10px] text-white/45 uppercase tracking-[0.22em]">
        {data.updated_at && <span>Updated {new Date(data.updated_at).toLocaleString("en-AU")}</span>}
       </div>
      </div>
     </div>

     {/* Tier cards */}
     {data.packages.map((tier, i) => {
      const isOpen = openTiers[tierKey(i)] !== false; // open by default
      return (
       <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`admin-tier-${activeType}-${i}`}>
        <div className="px-5 lg:px-7 py-4 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
         <button onClick={() => toggleTier(i)} className="inline-flex items-center gap-3 text-white hover:text-amber-300 transition-colors text-left">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="text-lg lg:text-xl font-black tracking-tight">{tier.name || `Tier ${i + 1}`}</span>
          <span className="text-amber-300 font-black text-lg tabular-nums">{tier.price}</span>
         </button>
         <button
          onClick={() => {
           if (!window.confirm(`Delete tier "${tier.name}"?`)) return;
           persist(activeType, (d) => { d.packages.splice(i, 1); return d; });
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-400/40 text-rose-200 hover:bg-rose-500/10 text-[10px] uppercase tracking-[0.18em] font-bold"
          data-testid={`admin-tier-${activeType}-${i}-delete`}
         >
          <Trash2 className="w-3 h-3" /> Delete tier
         </button>
        </div>

        {isOpen && (
         <div className="p-5 lg:p-7 space-y-6">
          {/* Core fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
           <Field label="Name" value={tier.name} onSave={(v) => persist(activeType, (d) => { d.packages[i].name = v; return d; })} testid={`admin-tier-${i}-name`} />
           <Field label="Price (display)" value={tier.price} onSave={(v) => persist(activeType, (d) => { d.packages[i].price = v; return d; })} testid={`admin-tier-${i}-price`} />
           <Field label="Sub-line (guests / duration)" value={tier.sub} onSave={(v) => persist(activeType, (d) => { d.packages[i].sub = v; return d; })} testid={`admin-tier-${i}-sub`} />
           <Field label="Extras / additional guests" value={tier.extras} onSave={(v) => persist(activeType, (d) => { d.packages[i].extras = v; return d; })} testid={`admin-tier-${i}-extras`} />
           <Field label="Price note (optional)" value={tier.priceNote || ""} onSave={(v) => persist(activeType, (d) => { d.packages[i].priceNote = v; return d; })} testid={`admin-tier-${i}-pricenote`} />
           <Field label="Menu heading" value={tier.menuTitle || ""} onSave={(v) => persist(activeType, (d) => { d.packages[i].menuTitle = v; return d; })} testid={`admin-tier-${i}-menutitle`} />
          </div>

          {/* Inclusions list */}
          <EditableList
           label="Inclusions"
           items={tier.inclusions || []}
           testidBase={`admin-tier-${i}-incl`}
           onChange={(next) => persist(activeType, (d) => { d.packages[i].inclusions = next; return d; })}
          />

          {/* Rules list (optional) */}
          {(tier.rules || []).length > 0 || tier.featured === false ? (
           <EditableList
            label="Rules / restrictions"
            items={tier.rules || []}
            testidBase={`admin-tier-${i}-rules`}
            onChange={(next) => persist(activeType, (d) => { d.packages[i].rules = next; return d; })}
           />
          ) : (
           <button
            onClick={() => persist(activeType, (d) => { d.packages[i].rules = [""]; return d; })}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-white/65 hover:bg-white/[0.06] text-[10px] uppercase tracking-[0.18em] font-bold"
            data-testid={`admin-tier-${i}-add-rules`}
           >
            <Plus className="w-3 h-3" /> Add rules section
           </button>
          )}

          {/* Menu by category */}
          <MenuEditor
           menu={tier.menu || {}}
           testidBase={`admin-tier-${i}-menu`}
           onChange={(next) => persist(activeType, (d) => { d.packages[i].menu = next; return d; })}
          />

          {/* Beverage list (optional) */}
          {(tier.beverage || []).length > 0 ? (
           <EditableList
            label="Beverage package"
            items={tier.beverage || []}
            testidBase={`admin-tier-${i}-bev`}
            onChange={(next) => persist(activeType, (d) => { d.packages[i].beverage = next; return d; })}
           />
          ) : (
           <button
            onClick={() => persist(activeType, (d) => { d.packages[i].beverage = [""]; return d; })}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-white/65 hover:bg-white/[0.06] text-[10px] uppercase tracking-[0.18em] font-bold"
            data-testid={`admin-tier-${i}-add-bev`}
           >
            <Plus className="w-3 h-3" /> Add beverage list
           </button>
          )}
         </div>
        )}
       </div>
      );
     })}

     <button
      onClick={() => persist(activeType, (d) => {
       d.packages.push({
        name: "New tier",
        price: "$0",
        sub: "Up to X guests · Y hour cruise",
        extras: "",
        inclusions: [],
        menuTitle: "",
        menu: {},
       });
       return d;
      })}
      className="w-full px-5 py-4 rounded-2xl border border-dashed border-white/20 text-white/70 hover:bg-white/[0.04] hover:text-white text-sm font-black uppercase tracking-[0.18em] transition-colors inline-flex items-center justify-center gap-2"
      data-testid={`admin-pkg-${activeType}-add-tier`}
     >
      <Plus className="w-4 h-4" /> Add a new tier
     </button>
    </div>
   )}
  </div>
 );
}

function Field({ label, value, onSave, testid }) {
 return (
  <div>
   <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/45 mb-1.5">{label}</div>
   <div className="text-sm text-white">
    <InlineEdit value={value || ""} onSave={onSave} testid={testid} />
   </div>
  </div>
 );
}

function EditableList({ label, items, onChange, testidBase }) {
 const addItem = () => onChange([...items, ""]);
 const updateItem = (i, v) => { const next = [...items]; next[i] = v; return onChange(next); };
 const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
 return (
  <div>
   <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/45 mb-2">{label}</div>
   <ul className="space-y-1.5">
    {items.map((it, i) => (
     <li key={i} className="flex items-start gap-2 group" data-testid={`${testidBase}-${i}`}>
      <span className="text-white/40 mt-1">·</span>
      <span className="flex-1 text-sm text-white/85">
       <InlineEdit
        value={it}
        onSave={(v) => updateItem(i, v)}
        placeholder="empty line"
        testid={`${testidBase}-${i}-text`}
       />
      </span>
      <button onClick={() => removeItem(i)} className="opacity-0 group-hover:opacity-100 text-white/45 hover:text-rose-300 transition-opacity p-1" data-testid={`${testidBase}-${i}-remove`}>
       <Trash2 className="w-3 h-3" />
      </button>
     </li>
    ))}
   </ul>
   <button onClick={addItem} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-white/65 hover:bg-white/[0.06] hover:text-white text-[10px] uppercase tracking-[0.18em] font-bold transition-colors" data-testid={`${testidBase}-add`}>
    <Plus className="w-3 h-3" /> Add line
   </button>
  </div>
 );
}

function MenuEditor({ menu, onChange, testidBase }) {
 const categories = useMemo(() => Object.keys(menu || {}), [menu]);
 const updateCategoryName = (oldName, newName) => {
  if (newName === oldName || !newName) return;
  const next = {};
  Object.entries(menu).forEach(([k, v]) => { next[k === oldName ? newName : k] = v; });
  onChange(next);
 };
 const updateItems = (cat, items) => onChange({ ...menu, [cat]: items });
 const removeCategory = (cat) => {
  const next = { ...menu }; delete next[cat]; onChange(next);
 };
 const addCategory = () => {
  let name = "New section";
  let n = 1;
  while (menu[name]) { n += 1; name = `New section ${n}`; }
  onChange({ ...menu, [name]: [""] });
 };
 return (
  <div>
   <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/45 mb-3">Menu</div>
   {categories.length === 0 ? (
    <div className="text-sm text-white/40 italic mb-3">No menu sections yet.</div>
   ) : (
    <div className="space-y-5">
     {categories.map((cat) => (
      <div key={cat} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4" data-testid={`${testidBase}-cat-${cat}`}>
       <div className="flex items-start justify-between gap-3 mb-2">
        <div className="text-white font-black text-sm tracking-wide">
         <InlineEdit
          value={cat}
          onSave={(v) => updateCategoryName(cat, v)}
          testid={`${testidBase}-cat-${cat}-name`}
         />
        </div>
        <button onClick={() => { if (window.confirm(`Delete menu section "${cat}"?`)) removeCategory(cat); }} className="text-white/40 hover:text-rose-300 p-1" data-testid={`${testidBase}-cat-${cat}-remove`}>
         <Trash2 className="w-3 h-3" />
        </button>
       </div>
       <EditableList
        label=""
        items={menu[cat] || []}
        onChange={(next) => updateItems(cat, next)}
        testidBase={`${testidBase}-cat-${cat}-items`}
       />
      </div>
     ))}
    </div>
   )}
   <button onClick={addCategory} className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 text-white/70 hover:bg-white/[0.06] hover:text-white text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid={`${testidBase}-add-cat`}>
    <Plus className="w-3 h-3" /> Add menu section
   </button>
  </div>
 );
}
